/* screens/support/SupportScreen.jsx — модуль «Поддержка» в консоли /console.
   Инбокс обращений (фильтр по статусу) + чат в дровере. Гейт RequireSupportAccess. */
import React from 'react';
import { PageHeader } from '../../components/patterns/PageHeader.jsx';
import { EmptyState, ErrorState } from '../../components/patterns/States.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { hooks } from '../../hooks/index.jsx';
import { RR } from '../../lib/router.jsx';
import * as api from '../../lib/api/support.js';
import { SupportThreadDrawer } from './SupportThreadDrawer.jsx';

const { useState, useCallback, useEffect, useRef } = React;
const { useFakeQuery } = hooks;
const { useSearchParams } = RR;

const FILTERS = [
  { value: 'open', label: 'Ждут ответа' },
  { value: 'pending', label: 'Ожидание' },
  { value: 'closed', label: 'Закрытые' },
  { value: '', label: 'Все' },
];
const STATUS = {
  open: { label: 'Ждут ответа', tone: 'accent' },
  pending: { label: 'Ожидание', tone: 'neutral' },
  closed: { label: 'Закрыта', tone: 'neutral' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU',
    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function SupportScreen() {
  const [params] = useSearchParams();
  const [filter, setFilter] = useState('open');
  const [nonce, setNonce] = useState(0);
  const [openId, setOpenId] = useState(null);
  const q = useFakeQuery('support-threads-' + filter + '-' + nonce,
    () => api.listThreads(filter || undefined), [filter, nonce]);
  const items = q.data || [];

  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  const closeDrawer = () => { setOpenId(null); refresh(); };

  // Deep-link из TG-уведомления оператору: /console/support?thread=ID → открыть заявку.
  const deepLinked = useRef(null);
  useEffect(() => {
    const tid = params.get('thread');
    if (tid && deepLinked.current !== tid) {
      deepLinked.current = tid;
      setOpenId(Number(tid));
    }
  }, [params]);

  return (
    <div className="section-page">
      <PageHeader breadcrumbs={[{ label: 'Админка', to: '/console' }, { label: 'Поддержка' }]}
        title="Поддержка" />
      <div className="section-body">
        <div className="cluster cluster-8" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <Button key={f.value || 'all'} size="sm"
              variant={filter === f.value ? 'primary' : 'ghost'}
              onClick={() => setFilter(f.value)}>{f.label}</Button>
          ))}
        </div>

        {q.loading ? (
          <div className="center pad-24"><Spinner /></div>
        ) : q.error ? (
          <ErrorState title="Не удалось загрузить" body={String(q.error.detail || q.error)} onRetry={refresh} />
        ) : items.length === 0 ? (
          <EmptyState icon="chat" title="Обращений нет"
            body="Здесь появятся обращения пользователей в поддержку." />
        ) : (
          <div className="stack stack-2">
            {items.map((t) => {
              const st = STATUS[t.status] || { label: t.status, tone: 'neutral' };
              return (
                <button type="button" key={t.id}
                  onClick={() => setOpenId(t.id)}
                  style={{ display: 'flex', gap: 12, alignItems: 'center', textAlign: 'left',
                           padding: '10px 12px', border: '1px solid var(--border, #e5e5e5)',
                           borderRadius: 10, background: t.operator_unread ? '#f5f8ff' : '#fff',
                           cursor: 'pointer', width: '100%' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 8, flex: '0 0 auto',
                                 background: t.operator_unread ? '#2f6bff' : 'transparent' }} />
                  <span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: 8,
                        flex: '0 0 auto', overflow: 'hidden', display: 'grid', placeItems: 'center',
                        background: 'var(--accent-soft, #eef2ff)', color: 'var(--accent, #2f6bff)',
                        fontWeight: 600, fontSize: 13 }}>
                    {t.user_avatar
                      ? <img src={t.user_avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : ((t.user_label || t.user_email || 'Г')[0] || '?').toUpperCase()}
                  </span>
                  <span className="stack stack-1 grow" style={{ minWidth: 0 }}>
                    <span className="truncate" style={{ fontWeight: 600 }}>
                      {t.user_label || t.user_email || 'Пользователь'}
                    </span>
                    <span className="t-caption text-mute truncate">{t.subject || t.preview || '—'}</span>
                  </span>
                  <Pill tone={st.tone} size="sm">{st.label}</Pill>
                  <span className="t-caption text-mute" style={{ flex: '0 0 auto' }}>
                    {fmtDate(t.last_message_at || t.created_at)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {openId != null && <SupportThreadDrawer threadId={openId} onClose={closeDrawer} />}
    </div>
  );
}

export { SupportScreen };
